import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import {
  initializeFirestore,
  getFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  collection,
  getDocs,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAcAtRSH-4ViApEgexixMtfowdPYVyryUc",
  authDomain: "mmacard-app-ee35d.firebaseapp.com",
  databaseURL: "https://mmacard-app-default-rtdb.firebaseio.com",
  projectId: "mmacard-app",
  storageBucket: "mmacard-app.appspot.com",
  messagingSenderId: "554893317291",
  appId: "1:554893317291:web:cea561ee1c40f8d27d13ee"
};

// Initialize Firebase and Firebase Authentication
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

/**
 * Wave 6 — App Check readiness gate.
 * Callables that enforce App Check must wait for init + first token
 * (missing/invalid App Check surfaces as functions/unauthenticated — NOT a
 * missing Auth JWT; httpsCallable still attaches request.auth when signed in).
 *
 * Localhost:
 * - Prefer REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN=<Console-registered UUID>
 * - Or set `true` once: Firebase prints a debug UUID in DevTools; register it
 *   under Console → App Check → Manage debug tokens, then replace `true` with
 *   that UUID and restart CRA. getToken failures never block Auth.
 * Prod: reCAPTCHA v3 site key only. Keep the AppCheck instance even if the
 * first getToken fails (mobile Safari often needs a short retry); getCallable
 * retries before invoke and fails closed if still tokenless.
 */
let resolveAppCheckReady;
const appCheckReady = new Promise((resolve) => {
  resolveAppCheckReady = resolve;
});
/** @type {import('firebase/app-check').AppCheck | null} */
let appCheckInstance = null;
/** True when local CRA skipped App Check (enforced callables will 401). */
let appCheckSkippedLocal = false;
/** True when prod/local init expected App Check but never got a usable instance. */
let appCheckInitFailed = false;

const APPCHECK_DEBUG_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const APPCHECK_VERIFY_MSG =
  "Couldn't verify this request. Please refresh and try again.";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * The App Check SDK's reCAPTCHA v3 script loader
 * (`loadReCAPTCHAV3Script` in @firebase/app-check) only wires `script.onload`
 * — there is NO `script.onerror` handler. If the request for
 * www.google.com/recaptcha/api.js is blocked outright (iCloud Private Relay,
 * a content blocker, DNS failure) rather than rejected, that script tag
 * never fires onload, `initializeV3()`'s internal Deferred never
 * resolves/rejects, and `getToken()` hangs **forever** — which would also
 * wedge `appCheckReady` (nothing downstream ever gets a token OR an error).
 * Race every getToken() call against a hard timeout so we always end up
 * with a classifiable failure instead of an infinite hang.
 */
const APPCHECK_TOKEN_TIMEOUT_MS = 7000;
function withAppCheckTimeout(promise, ms = APPCHECK_TOKEN_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error(
        `App Check getToken() timed out after ${ms}ms (recaptcha script likely blocked)`
      );
      err.code = 'appcheck/timeout-client';
      reject(err);
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * reCAPTCHA v3 rejects the client (bots, iCloud Private Relay, Safari private
 * browsing) → App Check exchange gets a 403 → the SDK sets an **in-memory**
 * throttle on the ReCaptchaV3Provider instance (`appCheck/initial-throttle`,
 * then `appCheck/throttled`) that blocks further attempts for a backoff
 * window (up to ~24h of backoff growth) until the page/app is reloaded.
 * See firebase/firebase-js-sdk#9135/#7371.
 *
 * BUT the 403-throttle path is only ONE failure mode. On mobile Safari the
 * far more common failure is the reCAPTCHA *script/badge itself* never
 * succeeding at all — blocked by iCloud Private Relay, content blockers,
 * ITP, or a slow/failed load of www.google.com/recaptcha — which the SDK
 * reports as `appCheck/recaptcha-error`, NOT a throttle code. There's also
 * `appCheck/fetch-network-error` when the exchange fetch can't reach the
 * network at all. All three are dead-ends worth surfacing distinctly instead
 * of collapsing to the generic "couldn't verify" message.
 */
function classifyAppCheckError(err) {
  const code = err?.code || '';
  const msg = err?.message || '';
  if (/^appcheck\/(initial-)?throttl/i.test(code) || /throttl/i.test(msg)) return 'throttled';
  if (code === 'appcheck/timeout-client') return 'timeout';
  if (/^appcheck\/recaptcha-error/i.test(code)) return 'recaptcha';
  if (/^appcheck\/fetch-network-error/i.test(code)) return 'network';
  if (/^appcheck\/fetch-(status|parse)-error/i.test(code)) return 'fetch';
  return 'unknown';
}

const APPCHECK_THROTTLED_MSG =
  "App Check is rate-limited on this device (reCAPTCHA flagged it — common with " +
  "iCloud Private Relay or Safari private browsing). Retrying won't help until you " +
  'reload in a normal (non-private) browser window, a different network/Wi-Fi, or ' +
  'approve from desktop.';

const APPCHECK_RECAPTCHA_MSG =
  "This device's reCAPTCHA check never completed (commonly iCloud Private Relay, " +
  'a content blocker, or Safari private browsing blocking google.com/recaptcha). ' +
  'Try turning off Private Relay/content blockers for this site, a different network, ' +
  'or approve from desktop.';

const APPCHECK_NETWORK_MSG =
  "Couldn't reach the App Check verification server (network blocked the reCAPTCHA/" +
  'App Check request). Try a different network/Wi-Fi or approve from desktop.';

const APPCHECK_TIMEOUT_MSG =
  "The reCAPTCHA check for App Check never finished loading (likely blocked by iCloud " +
  'Private Relay, a content blocker, or a slow/blocked connection to google.com/recaptcha). ' +
  'Try a normal browser window with Private Relay off, a different network, or approve from desktop.';

/** Map a classified App Check failure to admin-facing diagnostic copy. */
function describeAppCheckFailure(kind) {
  if (kind === 'throttled') return APPCHECK_THROTTLED_MSG;
  if (kind === 'recaptcha') return APPCHECK_RECAPTCHA_MSG;
  if (kind === 'network') return APPCHECK_NETWORK_MSG;
  if (kind === 'timeout') return APPCHECK_TIMEOUT_MSG;
  return APPCHECK_VERIFY_MSG;
}

/**
 * Obtain an App Check token with mobile-friendly retries.
 * Keeps the AppCheck instance alive across failed attempts so auto-refresh
 * and later getCallable retries can still succeed.
 * @returns {Promise<{token: string|null, kind: 'throttled'|'recaptcha'|'network'|'fetch'|'unknown'|null, lastErr: unknown}>}
 */
async function ensureAppCheckToken({ forceRefresh = false, retries = 4 } = {}) {
  await appCheckReady;
  if (!appCheckInstance) return { token: null, kind: null, lastErr: null };

  const { getToken } = await import('firebase/app-check');
  let lastErr;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      const result = await withAppCheckTimeout(
        getToken(appCheckInstance, /* forceRefresh */ forceRefresh || attempt > 0)
      );
      if (result?.token) return { token: result.token, kind: null, lastErr: null };
    } catch (err) {
      lastErr = err;
      const kind = classifyAppCheckError(err);
      if (kind === 'throttled') {
        // Backoff can be hours — further attempts are guaranteed to fail identically.
        console.warn('App Check throttled (reCAPTCHA rejection):', err?.message || err);
        return { token: null, kind, lastErr };
      }
      // Mobile Safari / PWA: reCAPTCHA script + first exchange often need a beat.
      await sleep(400 * (attempt + 1));
    }
  }
  if (lastErr) {
    console.warn(
      'App Check token unavailable after retries:',
      lastErr?.code || '',
      lastErr?.message || lastErr
    );
  }
  return { token: null, kind: lastErr ? classifyAppCheckError(lastErr) : null, lastErr };
}

if (typeof window !== 'undefined') {
  const siteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;
  const debugTokenRaw = (process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN || '').trim();
  const isLocal =
    process.env.NODE_ENV !== 'production' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  const isRegisteredDebugUuid = APPCHECK_DEBUG_UUID_RE.test(debugTokenRaw);
  const isDebugBootstrap =
    debugTokenRaw === 'true' || debugTokenRaw === '1';
  // Local: UUID or true/1 enables App Check debug provider. Prod: site key alone.
  const shouldInitAppCheck = Boolean(
    siteKey && (!isLocal || isRegisteredDebugUuid || isDebugBootstrap)
  );

  if (shouldInitAppCheck) {
    if (isLocal && (isRegisteredDebugUuid || isDebugBootstrap)) {
      // MUST be set synchronously before initializeAppCheck runs
      // (Firebase reads getGlobal().FIREBASE_APPCHECK_DEBUG_TOKEN).
      // Use window (not globalThis) — CRA eslint no-undef; same global in browsers.
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = isDebugBootstrap
        ? true
        : debugTokenRaw;
    }
    import('firebase/app-check')
      .then(async ({ initializeAppCheck, ReCaptchaV3Provider, getToken }) => {
        try {
          appCheckInstance = initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(siteKey),
            isTokenAutoRefreshEnabled: true,
          });
          // Warm token; never null the instance on failure — mobile needs retries.
          // Timeout-guarded — the SDK's script loader has no onerror handler, so a
          // fully-blocked recaptcha script load would otherwise hang this forever
          // and wedge appCheckReady (see withAppCheckTimeout above).
          try {
            await withAppCheckTimeout(getToken(appCheckInstance, /* forceRefresh */ false));
            console.info(
              isLocal
                ? isDebugBootstrap
                  ? 'App Check initialized (debug bootstrap — copy UUID from console, register it, set DEBUG_TOKEN=<uuid>)'
                  : 'App Check initialized (debug UUID path)'
                : 'App Check initialized (reCAPTCHA v3)'
            );
          } catch (tokenErr) {
            console.warn(
              'App Check first token unavailable (will retry on callable):',
              tokenErr?.message || tokenErr,
              isLocal
                ? isDebugBootstrap
                  ? '— Look for "App Check debug token" UUID above, register in Console, set DEBUG_TOKEN=<uuid>, restart.'
                  : '— Re-check the debug UUID in Console → App Check → Manage debug tokens.'
                : '— Check reCAPTCHA domains + hosting CSP (www.recaptcha.net / worker-src).'
            );
          }
        } catch (err) {
          appCheckInstance = null;
          appCheckInitFailed = true;
          if (isLocal) appCheckSkippedLocal = true;
          console.warn('App Check init skipped:', err?.message || err);
        }
      })
      .catch((err) => {
        appCheckInstance = null;
        appCheckInitFailed = true;
        if (isLocal) appCheckSkippedLocal = true;
        console.warn('App Check init skipped:', err?.message || err);
      })
      .finally(() => resolveAppCheckReady());
  } else {
    if (isLocal) {
      appCheckSkippedLocal = true;
      if (!siteKey) {
        console.warn(
          'App Check: REACT_APP_FIREBASE_APPCHECK_SITE_KEY unset — ' +
            'callables still enforcing App Check (logSecurityEvent, ' +
            'batchUpdateCards, adminDeleteAccount, …) will return ' +
            'functions/unauthenticated against prod. ' +
            'Set site key + a registered DEBUG_TOKEN UUID, or use emulators.'
        );
      } else {
        console.warn(
          'App Check skipped on localhost (DEBUG_TOKEN empty). ' +
            'Signed-in Auth still works, but callables still enforcing App ' +
            'Check (logSecurityEvent, batchUpdateCards, adminDeleteAccount, …) ' +
            'fail with Unauthenticated / "Couldn\'t verify this request". ' +
            'Fix: uuidgen → Console → App Check → Manage debug tokens → ' +
            'REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN=<uuid> in .env.local → restart CRA. ' +
            'Or set DEBUG_TOKEN=true once to print a UUID, then register it.'
        );
      }
    } else if (!siteKey) {
      appCheckInitFailed = true;
      console.warn(
        'App Check: REACT_APP_FIREBASE_APPCHECK_SITE_KEY missing from production build — ' +
          'enforced callables will fail verification.'
      );
    }
    resolveAppCheckReady();
  }
} else {
  resolveAppCheckReady();
}

/** Await App Check init + best-effort token. Never throws (Auth must stay usable). */
export async function awaitAppCheck() {
  await ensureAppCheckToken({ forceRefresh: false, retries: 3 });
}

function isAppCheckUnauthError(err) {
  const code = err?.code || '';
  if (code === 'functions/unauthenticated' || code === 'unauthenticated') {
    return true;
  }
  return (
    typeof err?.message === 'string' &&
    /couldn'?t verify this request/i.test(err.message)
  );
}

/**
 * Raw HTTPS invocation for admin callables deployed with `enforceAppCheck: false`
 * (they hard-check isAdminUid server-side instead — see submissionPipeline.js).
 *
 * Why not just call httpsCallable() with a missing token? Firebase's own
 * ContextProvider.getContext() (inside @firebase/functions) unconditionally calls
 * `this.appCheck.getToken()` before every invoke whenever an AppCheck instance is
 * registered on the app — even though our server doesn't require the header. That
 * internal call shares the SAME wedged exchange promise described above (no
 * timeout), so a normal httpsCallable() would hang forever on a device where App
 * Check is stuck, regardless of server-side enforcement. Bypassing
 * firebase/functions entirely and posting straight to the callable's HTTPS
 * endpoint with just the Firebase Auth ID token sidesteps that hang completely.
 */
async function callWithoutAppCheck(name, data) {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('Sign in required');
    err.code = 'functions/unauthenticated';
    throw err;
  }
  const idToken = await user.getIdToken();
  const projectId = app.options.projectId;
  const url = `https://us-central1-${projectId}.cloudfunctions.net/${name}`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ data: data === undefined ? null : data }),
    });
  } catch (fetchErr) {
    // Surface enough to tell "never reached the network" apart from a server error.
    const err = new Error(
      `Could not reach ${name} (network/CORS blocked the request): ${fetchErr?.message || fetchErr}`
    );
    err.code = 'functions/unavailable';
    err.cause = fetchErr;
    throw err;
  }
  const rawText = await res.text().catch(() => '');
  let json = null;
  try {
    json = rawText ? JSON.parse(rawText) : null;
  } catch (_) {
    // Non-JSON body — fall through to status-based error below, snippet included.
  }
  if (!res.ok) {
    const errBody = json && json.error;
    const status = typeof errBody?.status === 'string' ? errBody.status : '';
    const snippet = rawText.slice(0, 300);
    const message =
      errBody?.message ||
      (snippet
        ? `Request failed (HTTP ${res.status}): ${snippet}`
        : `Request failed (HTTP ${res.status})`);
    const err = new Error(message);
    err.code = status
      ? `functions/${status.toLowerCase().replace(/_/g, '-')}`
      : 'functions/internal';
    err.details = errBody?.details;
    err.httpStatus = res.status;
    throw err;
  }
  if (!json) {
    throw new Error(
      `Response is not valid JSON (HTTP ${res.status}): ${rawText.slice(0, 300)}`
    );
  }
  let responseData = json.data;
  if (typeof responseData === 'undefined') responseData = json.result;
  return { data: responseData };
}

/**
 * httpsCallable wrapper that waits for a usable App Check token before invoke.
 * Prod fails closed (no silent call without a token → cryptic CF unauthenticated).
 * On mobile Safari, reCAPTCHA exchange can race the first callable — retry once
 * with a forced token refresh when CF returns unauthenticated. If the SDK is
 * throttled (reCAPTCHA rejection), skip the pointless retry and say so.
 *
 * @param {string} name
 * @param {{ allowMissingAppCheck?: boolean }} [opts] Set true ONLY for callables
 *   deployed with `enforceAppCheck: false` because they already hard-gate on
 *   `request.auth` server-side (e.g. approveImageSubmission's isAdminUid check,
 *   addToUserCollection/removeFromUserCollection's authUserId === userId check,
 *   sendChatMessage/blockMessagingUser/markConversationUnread's participant
 *   checks, marketplace listing/deal/review callables' ownership/party checks,
 *   submitBugReport/reportUser's requireVerified, createOrUpdateUserProfile's
 *   authUserId === userId check, deleteOwnAccount's auth.uid-only target).
 *   Do NOT set for callables that have no real auth/ownership gate of their
 *   own and rely on App Check as their primary abuse control — those must
 *   keep failing closed without a token.
 *
 *   IMPORTANT: when true, this ALWAYS uses the raw-HTTPS admin fallback and never
 *   touches `httpsCallable()` — even if an App Check token happens to be available.
 *   Earlier versions tried ensureAppCheckToken() first and only fell back when it
 *   came back null; if App Check resolved successfully (or "successfully" but slow),
 *   getCallable would build a normal httpsCallable(), whose *internal*
 *   ContextProvider.getAppCheckToken() calls appCheck.getToken() a second time with
 *   NO timeout (see @firebase/functions ContextProvider) — reintroducing the exact
 *   mobile-Safari hang this fallback exists to avoid. Since the server ignores App
 *   Check entirely for these callables (enforceAppCheck:false, isAdminUid hard-check
 *   instead), there is no upside to ever attempting it here.
 */
export async function getCallable(name, { allowMissingAppCheck = false } = {}) {
  if (allowMissingAppCheck) {
    return (data) => callWithoutAppCheck(name, data);
  }

  const { token, kind } = await ensureAppCheckToken({
    forceRefresh: false,
    retries: 4,
  });

  if (!token) {
    if (appCheckSkippedLocal && !appCheckInstance) {
      console.warn(
        `[App Check] Calling "${name}" without a local App Check token — ` +
          'expect functions/unauthenticated. Set REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN ' +
          'to a Console-registered UUID (or true once to print one) and restart CRA.'
      );
    } else if (
      process.env.NODE_ENV === 'production' ||
      appCheckInitFailed ||
      appCheckInstance
    ) {
      // Production (or init attempted): don't hit CF without a token.
      const err = new Error(describeAppCheckFailure(kind));
      err.code = 'functions/unauthenticated';
      throw err;
    }
  }

  // Explicit region — v2 callables are us-central1 (matches deployed messaging/marketplace).
  const callable = httpsCallable(getFunctions(getApp(), 'us-central1'), name);

  return async (data) => {
    try {
      return await callable(data);
    } catch (err) {
      if (!isAppCheckUnauthError(err) || !appCheckInstance) throw err;
      const retry = await ensureAppCheckToken({ forceRefresh: true, retries: 4 });
      if (!retry.token) {
        if (retry.kind) {
          const diagErr = new Error(describeAppCheckFailure(retry.kind));
          diagErr.code = 'functions/unauthenticated';
          throw diagErr;
        }
        throw err;
      }
      return await callable(data);
    }
  };
}

// Set persistence globally with retry logic
const setPersistenceWithRetry = async (retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      await setPersistence(auth, browserLocalPersistence);
      console.log('Firebase auth persistence set successfully');
      break;
    } catch (err) {
      console.warn(`Failed to set auth persistence (attempt ${i + 1}):`, err);
      if (i === retries - 1) {
        console.error('Failed to set auth persistence after all retries');
      } else {
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
  }
};

setPersistenceWithRetry();

// Wave 6 — durable IndexedDB cache (multi-tab). Falls back to default if unsupported.
let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });
} catch (err) {
  console.warn('Persistent Firestore cache unavailable, using default:', err?.message || err);
  db = getFirestore(app);
}

const storage = getStorage(app);

// Reconnect signal for UX banner (config stays side-effect light)
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    window.dispatchEvent(new CustomEvent('fightdecks:online'));
  });
  window.addEventListener('offline', () => {
    window.dispatchEvent(new CustomEvent('fightdecks:offline'));
  });
}

const checkEmailUniqueness = async (email) => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    const existingEmails = querySnapshot.docs.map((doc) => doc.data().email);
    return !existingEmails.includes(email);
  } catch (error) {
    console.error('Error checking email uniqueness:', error);
    return false;
  }
};

const checkUsernameUniqueness = async (username, excludeUserId = null) => {
  try {
    if (!username || username.trim().length === 0) {
      return { isUnique: false, error: 'Username cannot be empty' };
    }

    const normalizedUsername = username.toLowerCase().trim();

    if (normalizedUsername.length < 3) {
      return { isUnique: false, error: 'Username must be at least 3 characters long' };
    }

    if (normalizedUsername.length > 20) {
      return { isUnique: false, error: 'Username must be 20 characters or less' };
    }

    const validUsernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validUsernameRegex.test(normalizedUsername)) {
      return { isUnique: false, error: 'Username can only contain letters, numbers, underscores, and hyphens' };
    }

    const reservedUsernames = [
      'admin', 'administrator', 'mod', 'moderator', 'api', 'www', 'ftp',
      'mail', 'email', 'support', 'help', 'info', 'contact', 'about',
      'fightdecks', 'fightdeck', 'ufc', 'system', 'root', 'user',
      'public', 'private', 'official', 'staff', 'team', 'news',
      'blog', 'forum', 'shop', 'store', 'marketplace', 'buy', 'sell',
      'trade', 'collection', 'profile', 'settings', 'account', 'login',
      'register', 'signup', 'signin', 'logout', 'dashboard', 'home'
    ];

    if (reservedUsernames.includes(normalizedUsername)) {
      return { isUnique: false, error: 'This username is reserved and cannot be used' };
    }

    const { isUsernameTaken } = await import('./publicProfileService');
    return isUsernameTaken(normalizedUsername, excludeUserId);
  } catch (error) {
    console.error('Error checking username uniqueness:', error);
    return { isUnique: false, error: 'Error validating username. Please try again.' };
  }
};

const getUserByUsername = async (username) => {
  const { fetchPublicProfileByUsername } = await import('./publicProfileService');
  return fetchPublicProfileByUsername(username);
};

export { checkUsernameUniqueness, getUserByUsername };
export { auth, db, checkEmailUniqueness, storage, appCheckReady };
